import type { SharedValue } from 'react-native-reanimated'
import { useCallback, useLayoutEffect } from 'react'
import { useWindowDimensions } from 'react-native'
import {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated'
import { scheduleOnRN } from 'react-native-worklets'
import { useRouter } from 'expo-router'

export type ProfileCardMode = 'visible' | 'hidden'

export interface UseOnboardingProfileCardAnimationParams {
  profileMode: ProfileCardMode
  keyboardOffset: SharedValue<number>
}

export function useOnboardingProfileCardAnimation({
  profileMode,
  keyboardOffset,
}: UseOnboardingProfileCardAnimationParams) {
  const router = useRouter()
  const { height: windowHeight } = useWindowDimensions()

  const isProfileInteractive = profileMode === 'visible'

  const panelHiddenOffset = windowHeight
  const profileTranslateY = useSharedValue(panelHiddenOffset)

  const handleProfileEnterComplete = useCallback(() => {
    router.setParams({ step: 'profile' })
  }, [router])

  const handleProfileExitComplete = useCallback(() => {
    router.setParams({ step: 'intro' })
  }, [router])

  useLayoutEffect(() => {
    if (profileMode === 'visible') {
      profileTranslateY.value = panelHiddenOffset
      profileTranslateY.value = withSpring(
        0,
        {
          damping: 20,
          stiffness: 190,
          mass: 0.9,
          velocity: 6,
          overshootClamping: false,
        },
        (finished) => {
          if (finished) {
            scheduleOnRN(handleProfileEnterComplete)
          }
        },
      )

      return
    }

    profileTranslateY.value = withTiming(
      panelHiddenOffset,
      {
        duration: 180,
        easing: Easing.in(Easing.cubic),
      },
      (finished) => {
        if (finished) {
          scheduleOnRN(handleProfileExitComplete)
        }
      },
    )
  }, [
    handleProfileEnterComplete,
    handleProfileExitComplete,
    panelHiddenOffset,
    profileMode,
    profileTranslateY,
  ])

  const profileCardAnimatedStyle = useAnimatedStyle(() => ({
    transform: [
      {
        translateY: profileTranslateY.value - keyboardOffset.value,
      },
    ],
  }))

  const handleBackToIntro = useCallback(() => {
    router.setParams({ step: 'intro' })
  }, [router])

  return {
    isProfileInteractive,
    profileCardAnimatedStyle,
    handleBackToIntro,
  }
}
